import { Chip, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { CheckCircle, ClockOutline } from '@opengov/react-capital-assets';

import { useLanguage, type Locale } from '../../contexts/LanguageContext';
import { useVcm } from '../../contexts/VcmContext';
import type { VcmModule } from '../../types/vcm';

const LANGUAGE_OPTIONS: ReadonlyArray<{ value: Locale; label: string }> = [
  { value: 'en', label: 'EN' },
  { value: 'es', label: 'ES' },
];

const MODULE_OPTIONS: ReadonlyArray<{ value: VcmModule; label: string }> = [
  { value: 'dcm', label: 'DCM' },
  { value: 'casework', label: 'Case Work' },
];

/**
 * Inline context switchers (language / module / role / event) + save status.
 * Rendered inside the single app navbar. Pure CDS/MUI.
 */
export function VcmControls() {
  const { module, role, availableRoles, setModule, setRole, saveStatus } = useVcm();
  const { locale, setLocale } = useLanguage();

  const toggleSx = { px: 2, fontSize: 12, fontWeight: 700 };
  const groupSx = { height: 32 };

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap>
      <SaveIndicator status={saveStatus} />

      <ToggleButtonGroup size="small" value={locale} exclusive onChange={(_, val) => { if (val) setLocale(val as Locale); }} sx={groupSx}>
        {LANGUAGE_OPTIONS.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value} sx={toggleSx}>{opt.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small" value={module} exclusive onChange={(_, val) => { if (val) setModule(val as VcmModule); }} sx={groupSx}>
        {MODULE_OPTIONS.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value} sx={toggleSx}>{opt.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        select
        size="small"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        sx={{
          minWidth: 170,
          '& .MuiInputBase-root': { height: 32, fontSize: 12, fontWeight: 700 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.divider },
        }}
      >
        {availableRoles.map((r) => (
          <MenuItem key={r} value={r} sx={{ fontSize: 12 }}>{r}</MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

export function SaveIndicator({ status }: { readonly status: string }) {
  if (status === 'saving') {
    return (
      <Chip
        icon={<ClockOutline />}
        label="Saving…"
        size="small"
        color="warning"
        variant="outlined"
      />
    );
  }
  if (status === 'saved') {
    return (
      <Chip icon={<CheckCircle />} label="Saved" size="small" color="success" variant="outlined" />
    );
  }
  if (status === 'error') {
    return <Chip label="Save failed" size="small" color="error" variant="outlined" />;
  }
  return null;
}
